<template>
    <v-container class="financeiro-page py-6">
        <v-card class="page-header mb-6" flat dark>
            <v-card-text class="pa-6 pa-md-8">
                <div class="d-flex flex-wrap align-center">
                    <v-avatar color="white" size="56" class="mr-4">
                        <v-icon color="teal darken-2" size="30">mdi-cash-multiple</v-icon>
                    </v-avatar>
                    <div>
                        <div class="overline font-weight-bold white--text">Gestão do negócio</div>
                        <h1 class="display-2 font-weight-bold mb-1">Controle financeiro</h1>
                        <p class="subtitle-1 mb-0 white--text text--lighten-4">
                            Receitas, despesas, comissões e lucro mês a mês.
                        </p>
                    </div>
                </div>
            </v-card-text>
        </v-card>

        <v-card class="mb-6 pa-4" outlined>
            <v-row align="center">
                <v-col cols="12" sm="4" md="3">
                    <v-text-field
                        v-model="mes"
                        type="month"
                        label="Mês de referência"
                        prepend-inner-icon="mdi-calendar"
                        outlined
                        dense
                        hide-details
                        @change="carregarTudo"
                    ></v-text-field>
                </v-col>

                <v-col cols="12" sm="8" md="5">
                    <div class="caption grey--text text--darken-1">
                        Última alteração
                    </div>
                    <div class="body-2 font-weight-medium">
                        {{ descricaoUltimaAlteracao }}
                    </div>
                </v-col>

                <v-col cols="12" md="4" class="text-md-right">
                    <v-btn
                        color="teal darken-2"
                        dark
                        :loading="carregando"
                        @click="carregarTudo"
                    >
                        <v-icon left>mdi-refresh</v-icon>
                        Recarregar
                    </v-btn>
                </v-col>
            </v-row>
        </v-card>

        <v-tabs
            v-model="abaSelecionada"
            background-color="transparent"
            color="teal darken-2"
            class="mb-6"
            show-arrows
        >
            <v-tab>Painel</v-tab>
            <v-tab>Consolidado</v-tab>
            <v-tab>Receitas</v-tab>
            <v-tab>Despesas</v-tab>
            <v-tab>Equipe e comissões</v-tab>
            <v-tab>Configurações</v-tab>
            <v-tab>
                <v-icon left small>mdi-help-circle-outline</v-icon>
                Ajuda
            </v-tab>
        </v-tabs>

        <v-progress-linear v-if="carregando" indeterminate color="teal"></v-progress-linear>

        <v-tabs-items v-model="abaSelecionada">
            <!-- ================= PAINEL ================= -->
            <v-tab-item>
                <div class="kpi-grid mb-6">
                    <div
                        v-for="kpi in indicadores"
                        :key="kpi.rotulo"
                        class="kpi-card"
                        :class="kpi.destaque ? 'kpi-card--destaque' : ''"
                    >
                        <div class="kpi-card__label">{{ kpi.rotulo }}</div>
                        <div class="kpi-card__value" :class="kpi.negativo ? 'kpi-card__value--negativo' : ''">
                            {{ kpi.valor }}
                        </div>
                        <div class="kpi-card__hint">{{ kpi.detalhe }}</div>
                    </div>
                </div>

                <v-row>
                    <v-col cols="12" md="5">
                        <v-card outlined class="pa-4 mb-4">
                            <div class="secao-titulo">Despesas por classificação</div>
                            <div class="caption grey--text text--darken-1 mb-3">
                                Sobre {{ formatarMoeda(resumo.despesasPagas) }} de despesas pagas no mês.
                            </div>

                            <div
                                v-for="item in resumo.porClassificacao"
                                :key="item.rotulo"
                                class="classificacao-item"
                            >
                                <div class="d-flex justify-space-between align-center">
                                    <span class="font-weight-medium">{{ item.rotulo }}</span>
                                    <span>
                                        {{ formatarMoeda(item.valor) }}
                                        <span class="grey--text caption ml-1">{{ formatarPercentual(item.percentual) }}</span>
                                    </span>
                                </div>
                                <v-progress-linear
                                    :value="item.percentual * 100"
                                    height="8"
                                    rounded
                                    color="teal darken-1"
                                    background-color="grey lighten-3"
                                    class="mt-1"
                                ></v-progress-linear>
                            </div>

                            <v-alert
                                v-if="resumo.despesasEmAberto > 0"
                                dense
                                text
                                type="warning"
                                class="mt-4 mb-0 caption"
                            >
                                {{ formatarMoeda(resumo.despesasEmAberto) }} em despesas ainda em aberto —
                                não entram no lucro realizado.
                            </v-alert>
                        </v-card>

                        <v-card outlined class="pa-4">
                            <div class="secao-titulo">Conferência do fechamento</div>
                            <div class="caption grey--text text--darken-1 mb-3">
                                Lançamentos incompletos que distorcem o resultado do mês.
                            </div>

                            <div
                                v-for="item in conferenciaMes.controles"
                                :key="item.rotulo"
                                class="d-flex justify-space-between align-center conferencia-item"
                            >
                                <span class="body-2">{{ item.rotulo }}</span>
                                <v-chip
                                    x-small
                                    :color="item.ok ? 'green lighten-4' : 'red lighten-4'"
                                    :text-color="item.ok ? 'green darken-3' : 'red darken-3'"
                                >
                                    {{ item.ok ? 'OK' : item.quantidade + ' a revisar' }}
                                </v-chip>
                            </div>
                        </v-card>
                    </v-col>

                    <v-col cols="12" md="7">
                        <v-card outlined class="pa-4 mb-4">
                            <div class="secao-titulo">Resultado por barbeiro</div>
                            <v-simple-table dense>
                                <template v-slot:default>
                                    <thead>
                                        <tr>
                                            <th class="text-left">Barbeiro</th>
                                            <th class="text-right">Atend.</th>
                                            <th class="text-right">Faturamento</th>
                                            <th class="text-right">Comissão</th>
                                            <th class="text-right">Líquido</th>
                                            <th class="text-right">% da meta</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="item in resumo.porBarbeiro" :key="item.id">
                                            <td class="font-weight-medium">{{ item.nome }}</td>
                                            <td class="text-right">{{ item.quantidade }}</td>
                                            <td class="text-right">{{ formatarMoeda(item.faturamento) }}</td>
                                            <td class="text-right">{{ formatarMoeda(item.comissao) }}</td>
                                            <td class="text-right">{{ formatarMoeda(item.liquido) }}</td>
                                            <td class="text-right">
                                                <span v-if="item.meta">{{ formatarPercentual(item.percentualMeta) }}</span>
                                                <span v-else class="grey--text caption">sem meta</span>
                                            </td>
                                        </tr>
                                        <tr v-if="!resumo.porBarbeiro.length">
                                            <td colspan="6" class="text-center grey--text py-4">
                                                Nenhum barbeiro com receita lançada neste mês.
                                            </td>
                                        </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </v-card>

                        <v-card outlined class="pa-4">
                            <div class="secao-titulo">Receita por categoria de serviço</div>
                            <v-simple-table dense>
                                <template v-slot:default>
                                    <thead>
                                        <tr>
                                            <th class="text-left">Categoria</th>
                                            <th class="text-right">Quantidade</th>
                                            <th class="text-right">Faturamento</th>
                                            <th class="text-right">Líquido</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="item in resumo.porCategoria" :key="item.categoria">
                                            <td class="font-weight-medium">{{ item.categoria }}</td>
                                            <td class="text-right">{{ item.quantidade }}</td>
                                            <td class="text-right">{{ formatarMoeda(item.faturamento) }}</td>
                                            <td class="text-right">{{ formatarMoeda(item.liquido) }}</td>
                                        </tr>
                                        <tr v-if="!resumo.porCategoria.length">
                                            <td colspan="4" class="text-center grey--text py-4">
                                                Nenhuma receita recebida neste mês.
                                            </td>
                                        </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </v-card>
                    </v-col>
                </v-row>
            </v-tab-item>

            <!-- ================= CONSOLIDADO ================= -->
            <v-tab-item>
                <FinanceiroConsolidado
                    :barbeiros="barbeiros"
                    :usuario="getCurrentUser"
                    :nomeBarbearia="config.nomeBarbearia"
                />
            </v-tab-item>

            <!-- ================= RECEITAS ================= -->
            <v-tab-item>
                <FinanceiroReceitas
                    :mes="mes"
                    :receitas="receitas"
                    :config="config"
                    :comissoes="comissoes"
                    :barbeiros="barbeiros"
                    :usuario="getCurrentUser"
                    @recarregar="carregarTudo"
                />
            </v-tab-item>

            <!-- ================= DESPESAS ================= -->
            <v-tab-item>
                <FinanceiroDespesas
                    :mes="mes"
                    :despesas="despesas"
                    :config="config"
                    :usuario="getCurrentUser"
                    @recarregar="carregarTudo"
                />
            </v-tab-item>

            <!-- ================= EQUIPE ================= -->
            <v-tab-item>
                <FinanceiroEquipe
                    :comissoes="comissoes"
                    :barbeiros="barbeiros"
                    :usuario="getCurrentUser"
                    @recarregar="carregarTudo"
                />
            </v-tab-item>

            <!-- ================= CONFIGURAÇÕES ================= -->
            <v-tab-item>
                <FinanceiroConfiguracoes
                    :config="config"
                    :usuario="getCurrentUser"
                    @recarregar="carregarTudo"
                />
            </v-tab-item>

            <!-- ================= AJUDA ================= -->
            <v-tab-item>
                <FinanceiroAjuda />
            </v-tab-item>
        </v-tabs-items>
    </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { refBarbeiros } from '@/firebaseConfig'
import {
    carregarConfig,
    carregarComissoes,
    listarReceitas,
    listarDespesas,
    sincronizarResumoMensal
} from '@/services/financeiro'
import {
    CONFIG_PADRAO,
    resumoMensal,
    conferencia,
    formatarMoeda,
    formatarPercentual,
    chaveMes
} from '@/services/financeiroCalculos'
import FinanceiroConsolidado from './Components/FinanceiroConsolidado'
import FinanceiroReceitas from './Components/FinanceiroReceitas'
import FinanceiroDespesas from './Components/FinanceiroDespesas'
import FinanceiroEquipe from './Components/FinanceiroEquipe'
import FinanceiroConfiguracoes from './Components/FinanceiroConfiguracoes'
import FinanceiroAjuda from './Components/FinanceiroAjuda'

export default {
    components: {
        FinanceiroConsolidado,
        FinanceiroReceitas,
        FinanceiroDespesas,
        FinanceiroEquipe,
        FinanceiroConfiguracoes,
        FinanceiroAjuda
    },

    data(){
        return {
            abaSelecionada: 0,
            carregando: false,
            mes: chaveMes(new Date().toISOString()),
            config: { ...CONFIG_PADRAO },
            comissoes: {},
            barbeiros: [],
            receitas: [],
            despesas: []
        }
    },

    computed: {
        ...mapGetters(['getCurrentUser']),

        resumo(){
            return resumoMensal(this.receitas, this.despesas, this.config, this.comissoes, this.barbeiros)
        },

        conferenciaMes(){
            return conferencia(this.receitas, this.despesas, this.comissoes)
        },

        indicadores(){
            const resumo = this.resumo

            return [
                {
                    rotulo: 'Faturamento bruto',
                    valor: this.formatarMoeda(resumo.faturamentoBruto),
                    detalhe: `${resumo.quantidadeRecebidos} lançamento(s) recebido(s)`
                },
                {
                    rotulo: 'Descontos',
                    valor: this.formatarMoeda(resumo.descontos),
                    detalhe: 'Abatido antes de taxas e comissões'
                },
                {
                    rotulo: 'Taxas de cartão',
                    valor: this.formatarMoeda(resumo.taxasCartao),
                    detalhe: 'Débito/crédito configurados ou taxa manual lançada em Despesas'
                },
                {
                    rotulo: 'Comissões',
                    valor: this.formatarMoeda(resumo.comissoes),
                    detalhe: 'Percentual por barbeiro e categoria'
                },
                {
                    rotulo: 'Receita após comissões',
                    valor: this.formatarMoeda(resumo.receitaAposComissoes),
                    detalhe: 'Bruto − descontos − taxas − comissões'
                },
                {
                    rotulo: 'Despesas operacionais',
                    valor: this.formatarMoeda(resumo.despesasOperacionais),
                    detalhe: resumo.despesasEmAberto
                        ? `+ ${this.formatarMoeda(resumo.despesasEmAberto)} em aberto`
                        : 'Taxas de cartão ficam no indicador acima'
                },
                {
                    rotulo: 'Lucro do mês',
                    valor: this.formatarMoeda(resumo.lucro),
                    detalhe: 'Receita após comissões − despesas pagas',
                    destaque: true,
                    negativo: resumo.lucro < 0
                },
                {
                    rotulo: 'Margem líquida',
                    valor: this.formatarPercentual(resumo.margem),
                    detalhe: `Meta: ${this.formatarPercentual(resumo.metaMargem)}${resumo.atingiuMetaMargem ? ' — atingida' : ''}`,
                    destaque: true,
                    negativo: resumo.margem < 0
                }
            ]
        },

        descricaoUltimaAlteracao(){
            const candidatos = [...this.receitas, ...this.despesas, this.config]
                .filter((item) => item && item.atualizadoEm)
                .sort((a, b) => a.atualizadoEm > b.atualizadoEm ? -1 : 1)

            if(!candidatos.length){
                return 'Nenhum lançamento salvo ainda neste mês.'
            }

            const ultimo = candidatos[0]
            const data = new Date(ultimo.atualizadoEm)
            return `${ultimo.atualizadoPorNome || ultimo.atualizadoPor} em ${data.toLocaleString('pt-BR')}`
        }
    },

    async created(){
        await this.carregarTudo()
    },

    methods: {
        formatarMoeda,
        formatarPercentual,

        async carregarTudo(){
            if(!this.mes){
                return
            }

            this.carregando = true
            try {
                const barbeirosSnap = await refBarbeiros.get()
                this.barbeiros = barbeirosSnap.docs
                    .map((doc) => ({ ...doc.data(), id: doc.data().id || doc.id }))
                    .sort((a, b) => (a.nome || '') > (b.nome || '') ? 1 : -1)

                const [config, comissoes, receitas, despesas] = await Promise.all([
                    carregarConfig(),
                    carregarComissoes(this.barbeiros),
                    listarReceitas(this.mes),
                    listarDespesas(this.mes)
                ])

                this.config = config
                this.comissoes = comissoes
                this.receitas = receitas
                this.despesas = despesas

                await this.atualizarConsolidado()
            } catch (error) {
                this.$toasted.global.defaultError({ msg: this.mensagemDeErro(error) })
            } finally {
                this.carregando = false
            }
        },

        // Mantém o cache consolidado do mês em dia. Só grava quando os números
        // realmente mudaram, então abrir a tela repetidas vezes não gera escrita.
        // Como roda a cada carregamento, um cache defasado se conserta sozinho.
        async atualizarConsolidado(){
            try {
                const resumo = resumoMensal(
                    this.receitas, this.despesas, this.config, this.comissoes, this.barbeiros
                )
                await sincronizarResumoMensal(this.mes, resumo, this.getCurrentUser)
            } catch (error) {
                // O consolidado é um cache: falhar aqui não pode derrubar a tela.
                console.log('Não foi possível atualizar o consolidado do mês:', error.message)
            }
        },

        mensagemDeErro(error){
            if(error && error.code === 'permission-denied'){
                return 'Sem permissão para acessar o controle financeiro. Confira o nível do seu usuário.'
            }
            return (error && error.message) || 'Não foi possível carregar o controle financeiro.'
        }
    }
}
</script>

<style scoped>
.financeiro-page {
    max-width: 1280px;
}

.page-header {
    background: linear-gradient(135deg, #00695c 0%, #26a69a 100%) !important;
    border-radius: 24px 24px 24px 6px;
}

.kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
}

.kpi-card {
    padding: 16px 18px;
    border-radius: 16px;
    background: linear-gradient(180deg, #f7fbfa 0%, #eef5f4 100%);
    border: 1px solid #d5e5e2;
}

.kpi-card--destaque {
    background: linear-gradient(180deg, #e8f5e9 0%, #dcefdd 100%);
    border-color: #a5d6a7;
}

.kpi-card__label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #5f7a76;
    margin-bottom: 6px;
}

.kpi-card__value {
    font-size: 24px;
    line-height: 1.1;
    font-weight: 800;
    color: #14312d;
    overflow-wrap: anywhere;
}

.kpi-card__value--negativo {
    color: #c62828;
}

.kpi-card__hint {
    margin-top: 6px;
    font-size: 12px;
    color: #6b8480;
}

.secao-titulo {
    font-size: 18px;
    font-weight: 800;
    color: #14312d;
    margin-bottom: 2px;
}

.classificacao-item + .classificacao-item {
    margin-top: 12px;
}

.conferencia-item {
    padding: 6px 0;
    border-top: 1px solid #eceff1;
}

@media (max-width: 960px) {
    .kpi-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 600px) {
    .page-header {
        border-radius: 18px 18px 18px 4px;
    }

    .page-header .display-2 {
        font-size: 2.1rem !important;
    }

    .kpi-grid {
        grid-template-columns: 1fr;
    }
}
</style>
